import axios from 'axios'
import jwt from 'jsonwebtoken'
import { getDeviceInfoBasedOnBrowser } from '../util/utilitys'
import { buildNumber } from '../constants/buildNumber.json'

import FirebaseAuthManager from '../fronted-share-components/src/utils/firebaseAuthManager'
import Observable from '../helpers/observerable'
import LocalStorageManager from './localStorageManager'

const { vendor, osVersion } = getDeviceInfoBasedOnBrowser()
const customUserAgent = `Alfred-Web/${buildNumber} (${vendor}; ${osVersion})`

export default class APIManager {
    static instance: APIManager = null
    JID = null
    APIInstance = null
    email = null
    rawInstance = null
    isWaitingForToken = false

    static getInstance() {
        if (!this.instance) {
            this.instance = new APIManager()
        }
        return this.instance
    }

    constructor() {
        let timeoutTime = 10 * 60 * 1000
        let APIUrl = 'https://rabbithole.my-alfred.com/v2.5'
        // let APIUrl = 'http://localhost:3001/v2.5';
        if (window.env === 'prod' || window.env === 'stage') {
            APIUrl = 'https://api.my-alfred.com/v2.5'
            timeoutTime = 60 * 60 * 1000
        }
        if (window.env === 'dev') {
            APIUrl = 'https://rabbithole.my-alfred.com/v2.5'
            timeoutTime = 10 * 60 * 1000
        }
        this.localStorageManager = LocalStorageManager.getInstance()
        this.APIUrl = APIUrl
        this.timeoutTime = timeoutTime
        this.observable = new Observable()
    }

    async exchangeKVtokenByRefreshToken({ refreshToken, jid, email, uid }) {
        const start = Date.now()
        const res = await axios.post(
            `${this.APIUrl}/user/authbyRefreshToken?version=${buildNumber}&jid=${jid}`,
            {
                refreshToken,
                jid,
                email,
            }
        )

        const rawInstance = this.createNewAPIInstance({
            refreshToken: res.data.refreshToken,
            kvToken: res.data.kv_token,
            email,
            jid,
            exp: start + this.timeoutTime,
            uid,
        })

        return {
            jid,
            kvToken: res.data.kv_token,
            rawInstance,
        }
    }

    async restoreToken({ idToken, jid, email }) {
        const auth = this.localStorageManager.getAuth()
        if (auth && auth.kvToken && auth.expireAt && auth.refreshToken) {
            if (auth.jid === jid) {
                this.JID = jid
                this.email = email
                const rawInstance = this.createNewAPIInstance({
                    refreshToken: auth.refreshToken,
                    kvToken: auth.kvToken,
                    email,
                    jid,
                    exp: auth.expireAt,
                    uid: auth.uid,
                })
                return {
                    jid,
                    kvToken: auth.kvToken,
                    rawInstance,
                }
            }
        }
        return this.getAccessToken({ idToken, jid, email })
    }

    async getAccessToken({ idToken, jid, email }) {
        this.JID = jid
        this.email = email
        const start = Date.now()
        const res = await axios.post(
            `${this.APIUrl}/user/authbytoken?version=${buildNumber}&jid=${jid}`,
            {
                type: 'fir_token',
                token: idToken,
                jid,
            },
            {
                headers: {
                    'x-User-Agent': customUserAgent,
                },
            }
        )

        const rawInstance = this.createNewAPIInstance({
            refreshToken: res.data.refreshToken,
            kvToken: res.data.kv_token,
            email,
            jid: this.JID,
            exp: start + this.timeoutTime,
            uid: res.data.uid,
        })

        return {
            jid,
            kvToken: res.data.kv_token,
            rawInstance,
        }
    }

    createNewAPIInstance({ refreshToken, kvToken, email, jid, exp, uid }) {
        const uniqueId = uid || jwt.decode(kvToken).unique_id
        const instance = axios.create({
            baseURL: this.APIUrl,
            timeout: 5000,
            headers: {
                Authorization: `Bearer ${kvToken}`,
                'x-User-Agent': customUserAgent,
            },
        })
        instance.interceptors.request.use(function (config) {
            const params = config.params || new URLSearchParams()
            params.set('version', buildNumber)
            params.set('uid', uniqueId)
            config.params = params
            return config
        })
        instance.refreshToken = refreshToken
        instance.email = email
        instance.jid = jid
        instance.exp = exp
        instance.uid = uniqueId

        this.rawInstance = instance
        this.refreshToken = refreshToken
        this.kvToken = kvToken
        this.APIInstance = new Proxy(instance, CustomAPIClientProxyHandler)
        this.localStorageManager.setAuth(
            jid,
            refreshToken,
            kvToken,
            exp,
            uniqueId
        )

        return instance
    }

    async createEventWithSnapshot({
        jid,
        device,
        version,
        snapshotName,
        snapshotData,
        label,
        type,
        storageDuration,
        serviceType,
        vidType,
    }) {
        const { url, pathname, bucket, provider } = await this.createUploadUrl(
            snapshotName
        )

        await axios.put(url, snapshotData.data, {
            headers: {
                'Content-Type': 'image/jpeg',
            },
        })

        const res = await this.APIInstance.post('/activity', {
            storage_duration: storageDuration,
            service_type: serviceType,
            type,
            jid,
            device,
            label,
            version,
            snapshot_name: pathname,
            range: snapshotData.thumbnail_range,
            webrtc: true,
            vtype: vidType == 'mp4' ? 'mp4_opus' : 'webm',
            bucket,
            provider,
        })

        if (!res.data || res.data.status !== true) {
            throw Error('UploadSnapshotFailed')
        }

        return res.data.multicast_id
    }

    async attachVideoToEvent({
        eventId,
        videoName,
        videoData,
        vsize,
        duration,
        device,
        label,
        type,
        vidType,
    }) {
        if (!eventId) return

        const startUploadTime = new Date()
        const { url, pathname } = await this.createUploadUrl(videoName)

        await axios.put(url, videoData, {
            headers: {
                'Content-Type': vidType == 'mp4' ? 'video/mp4' : 'video/webm',
            },
        })

        const uploadFinishTime = new Date() - startUploadTime

        const res = await this.APIInstance.post(`/activity/${eventId}/video`, {
            type,
            video: pathname,
            vsize,
            duration,
            device,
            label,
            payload: {
                upload_duration: uploadFinishTime,
            },
            vtype: vidType == 'mp4' ? 'mp4_opus' : 'webm',
            webrtc: true,
        })

        if (!res.data || res.data.status !== true) {
            throw Error('UploadVideoFailed')
        }
    }

    async createUploadUrl(filename) {
        const res = await this.APIInstance.post('/activity2/image', {
            filename,
        })
        const { url, pathname, bucket, provider } = res.data

        return {
            url,
            pathname,
            bucket,
            provider,
        }
    }

    async getIceServers() {
        const res = await this.APIInstance.get('/twilio')
        if (res.data.status === true) {
            return res.data.iceServers
        } else {
            return []
        }
    }

    async checkTrustCircle({ sharedUniqueId }) {
        const res = await this.APIInstance.get(
            `/device/camera/${encodeURIComponent(
                this.JID
            )}/share/verify?uniqueId=${sharedUniqueId}`
        )
        return res.data.status
    }

    async createUser({ email, name, fname, lname, picture, lang }) {
        const newAPI = this.APIUrl.replace('v2.5', 'v2.7')
        const res = await this.APIInstance.post(`${newAPI}/user/web`, {
            email,
            name,
            fname,
            lname,
            picture,
            lang,
        })
        return res.data
    }
    /**
     *
     *
     * @param {Object} Params
     * @param {String} Params.jid
     * @param {String} Params.deviceName,
     * @param {String} Params.appversion,
     * @param {String} Params.app,
     * @param {String} Params.os,
     * @param {String} Params.osversion,
     * @param {String} Params.vendor,
     * @param {String} [Params.type = "camera"]
     * @memberof APIManager
     */
    async registerDevice({
        jid,
        deviceName,
        appversion,
        app,
        os,
        osversion,
        vendor,
        type = 'camera',
    }) {
        await this.APIInstance.post('/device', {
            type,
            jid,
            cameras: [],
            device: deviceName,
            preview: true,
            webrtc: true,
            appversion,
            app,
            os,
            osversion,
            vendor,
            localtime: Date.now(),
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
    }

    async updateDevice({ motion, sensitivity, detector }) {
        await this.APIInstance.put(`/device/${encodeURIComponent(this.JID)}`, {
            motion,
            sensitivity,
            detector,
        })
    }

    async getDeviceData() {
        const res = await this.APIInstance.get(
            `/device/${encodeURIComponent(this.JID)}`
        )
        return res.data
    }

    async reportConnectionLog(phoneLog) {
        await this.APIInstance.post('/connection/log', phoneLog)
    }

    async getIdToken(forceRefresh) {
        const firebaseAuthManager = new FirebaseAuthManager()
        const user = firebaseAuthManager.getCurrentUser()
        if (user && user.emailVerified === true && user.refreshToken) {
            return user.getIdToken(forceRefresh)
        }
        return null
    }

    async updateAccessToken(jid, email, forceRefresh) {
        const idToken = await this.getIdToken(forceRefresh)
        if (idToken) {
            const { rawInstance } = await this.getAccessToken({
                idToken,
                jid,
                email,
            })
            return rawInstance
        } else {
            this.observable.notify({
                type: 'FORCE_SIGN_OUT',
            })
            throw new Error('The id token does not exist.')
        }
    }

    async updateToken(
        refreshToken = this.refreshToken,
        email = this.email,
        jid = this.JID,
        uid
    ) {
        this.isWaitingForToken = true
        try {
            const { rawInstance } = await this.exchangeKVtokenByRefreshToken({
                refreshToken,
                email,
                jid,
                uid,
            })
            this.isWaitingForToken = false
            return rawInstance
        } catch (err) {
            let error = err
            const res = err.response
            if (res?.status === 401 && res?.data?.error_code === 40102) {
                try {
                    const res = await this.updateAccessToken(jid, email)
                    this.isWaitingForToken = false
                    return res
                } catch (err) {
                    const res = err.response
                    error = err
                    if (res?.status === 403 || res?.status === 500) {
                        try {
                            const res = await this.updateAccessToken(
                                jid,
                                email,
                                true
                            )
                            this.isWaitingForToken = false
                            return res
                        } catch (err) {
                            const res = err.response
                            error = err
                            if (res?.status === 403 || res?.status === 500) {
                                this.observable.notify({
                                    type: 'FORCE_SIGN_OUT',
                                })
                            }
                        }
                    }
                }
            } else if (res?.status === 403 && res?.data?.error_code === 40399) {
                this.observable.notify({
                    type: 'FORCE_SIGN_OUT',
                })
            }
            this.isWaitingForToken = false
            throw error
        }
    }

    async getUser() {
        const newAPI = this.APIUrl.replace('v2.5', 'v3.0')
        const res = await this.APIInstance.get(`${newAPI}/user`)
        return res.data
    }
}

const getUpdatedInstance = async () => {
    const apiManager = APIManager.getInstance()
    if (apiManager.isWaitingForToken) {
        await new Promise((res, rej) => {
            const check = () => {
                try {
                    if (!apiManager.isWaitingForToken) {
                        return res()
                    } else {
                        // delay 50ms
                        setTimeout(check, 50)
                    }
                } catch (err) {
                    return rej(err)
                }
            }
            check()
        })
        return apiManager.rawInstance
    }
    return null
}

const CustomAPIClientProxyHandler = {
    get(target, propKey, receiver) {
        const origMethod = target[propKey]

        return async (...args) => {
            try {
                const apiManager = APIManager.getInstance()
                const newInstance = await getUpdatedInstance()
                if (newInstance) {
                    return await newInstance[propKey].apply(apiManager, args)
                }
                const exp = target.exp
                if (Date.now() > exp) {
                    const rawInstance = await apiManager.updateToken(
                        target.refreshToken,
                        target.email,
                        target.jid,
                        target.uid
                    )
                    return await rawInstance[propKey].apply(apiManager, args)
                } else {
                    const res = await origMethod.apply(this, args)
                    return res
                }
            } catch (error) {
                handleCustomError(error)

                if (error.response && error.response.status === 403) {
                    const apiManager = APIManager.getInstance()
                    const newInstance = await getUpdatedInstance()
                    if (newInstance) {
                        return await newInstance[propKey].apply(
                            apiManager,
                            args
                        )
                    }
                    const rawInstance = await apiManager.updateToken(
                        target.refreshToken,
                        target.email,
                        target.jid,
                        target.uid
                    )
                    return await rawInstance[propKey].apply(apiManager, args)
                } else {
                    return await origMethod.apply(this, args)
                }
            }
        }
    },
}

function handleCustomError(error) {
    if (
        !navigator.onLine ||
        /(Network Error)|(ERR_INTERNET_DISCONNECTED)/.test(error)
    ) {
        throw 'NetworkError'
    }

    if (error.response && error.response.status === 500) {
        throw 'APIServerError'
    }
}
